import { CheckCircle, Clock, Target, TrendingUp } from 'lucide-react';

export function ProgressTracker({ stats, horizontal = false }) {
  if (horizontal) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Progress Bar Section */}
          <div className="md:col-span-2">
            <h3 className="text-sm font-semibold text-card-foreground mb-2">Overall Progress</h3>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-2xl font-bold text-foreground">{stats.completionPercentage || 0}%</span>
                <span className="text-sm text-muted-foreground">
                  {stats.completedActions || 0} of {stats.totalActions || 0} tasks
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${stats.completionPercentage || 0}%` }}
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <Clock className="w-5 h-5 text-blue-600 mr-1" />
                <span className="text-2xl font-bold text-blue-600">{stats.inProgressActions || 0}</span>
              </div>
              <span className="text-sm text-muted-foreground">In Progress</span>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <Target className="w-5 h-5 text-red-600 mr-1" />
                <span className="text-2xl font-bold text-red-600">{stats.highImpactCompleted || 0}</span>
              </div>
              <span className="text-sm text-muted-foreground">High Impact Done</span>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <TrendingUp className="w-5 h-5 text-purple-600 mr-1" />
                <span className="text-2xl font-bold text-purple-600">{Math.round((stats.estimatedTimeRemaining || 0) / 60)}h</span>
              </div>
              <span className="text-sm text-muted-foreground">Time Remaining</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Original vertical layout
  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-lg font-semibold text-foreground mb-4">Progress Overview</h2>
      
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-card-foreground">Overall Completion</span>
          <span className="text-sm font-medium text-foreground">{stats.completionPercentage || 0}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${stats.completionPercentage || 0}%` }}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 mb-6">
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <CheckCircle className="w-5 h-5 text-green-600 mr-1" />
            <span className="text-2xl font-bold text-green-600">{stats.completedActions || 0}</span>
          </div>
          <span className="text-sm text-muted-foreground">Completed</span>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <Clock className="w-5 h-5 text-blue-600 mr-1" />
            <span className="text-2xl font-bold text-blue-600">{stats.inProgressActions || 0}</span>
          </div>
          <span className="text-sm text-muted-foreground">In Progress</span>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <Target className="w-5 h-5 text-red-600 mr-1" />
            <span className="text-2xl font-bold text-red-600">{stats.highImpactCompleted || 0}</span>
          </div>
          <span className="text-sm text-muted-foreground">High Impact Done</span>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <TrendingUp className="w-5 h-5 text-purple-600 mr-1" />
            <span className="text-2xl font-bold text-purple-600">{Math.round((stats.estimatedTimeRemaining || 0) / 60)}h</span>
          </div>
          <span className="text-sm text-muted-foreground">Time Remaining</span>
        </div>
      </div>

      {/* Next Recommendation */}
      <div className="bg-accent border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">🎯 Next Recommended Action</h3>
        <p className="text-sm text-blue-800">
          Complete 2 more high-impact actions to trigger AI citation improvements within the next 2 weeks.
        </p>
      </div>
    </div>
  );
}